import { useEffect, useMemo } from 'react';

import { ChakraBox, ChakraFlex, EquipUIFireflyV2Theme } from '@equip.health/ui';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { DateTime } from 'luxon';

import {
  WEEKDAYS_ALTERNATIVE,
  WEEK_VIEW_TIMES,
} from '~/lib/constants/calendar.constants';
import { getUSWeekStartMonday } from '~/lib/util/calendar.util';
import {
  getWeekViewDayLayout,
  WeekViewLayoutUnit,
} from '~/lib/util/schedule/weekViewLayout.util';

import ScheduleWeekBodyItem from './ScheduleWeekBodyItem';
import ScheduleWeekHeaderItem from './ScheduleWeekHeaderItem';
import ScheduleWeekTimeItem from './ScheduleWeekTimeItem';

const TOP_LEVEL_ID = 'schedule-week-view';
const timesContainerId = `${TOP_LEVEL_ID}__times-container`;
const { neutral } = EquipUIFireflyV2Theme.colors;

type DayHeaderItemType = {
  date: DateTime;
  weekday: string;
};

export const scrollToCurrentTime = (scheduledItems: ScheduleItemBase[]) => {
  const WEEK_VIEW_8AM_INDEX = 8;
  const WEEK_VIEW_12AM_ELEMENT_ID = 'week-time-item__12 AM';
  const earliestEventStartTime = scheduledItems
    .map((item) => ({
      ...item,
      startDateTime: DateTime.fromISO(item.startDateTime),
    }))
    .sort(
      (a, b) =>
        a.startDateTime.hour * 60 +
        a.startDateTime.minute -
        (b.startDateTime.hour * 60 + b.startDateTime.minute),
    )[0]?.startDateTime;

  const earliestEventStartTimeIndex = WEEK_VIEW_TIMES.findIndex(
    (time) => time === earliestEventStartTime?.toFormat('h a'),
  );

  const finalWeekViewTimeIndex =
    earliestEventStartTimeIndex !== -1
      ? earliestEventStartTimeIndex
      : WEEK_VIEW_8AM_INDEX;

  const weekView12amElementOffsetHeight = document.getElementById(
    WEEK_VIEW_12AM_ELEMENT_ID,
  ).offsetHeight;

  const timesContainer = document.getElementById(timesContainerId);

  const scrollToEventOffsetHeight =
    (weekView12amElementOffsetHeight == 0
      ? 48
      : weekView12amElementOffsetHeight) * finalWeekViewTimeIndex;

  if (timesContainer && timesContainer.scrollBy) {
    timesContainer.scrollBy({
      behavior: 'smooth',
      top: scrollToEventOffsetHeight,
    });
  } else if (timesContainer) {
    timesContainer.scrollTop += scrollToEventOffsetHeight;
  }
};

interface ScheduleWeekViewProps {
  currentDate: DateTime;
  onCancelledGroupClicked?: (startDateTime: string) => void;
  onScheduleItemClicked: (externalId: string) => void;
  scheduledItems: ScheduleItemBase[];
  height?: string;
  isProvider?: boolean;
}

const ScheduleWeekView = ({
  currentDate,
  isProvider,
  onCancelledGroupClicked,
  onScheduleItemClicked,
  scheduledItems,
  height,
}: ScheduleWeekViewProps) => {
  const {
    viewCancelledAppointmentsInMaud: isViewCancelledAppointmentsInMaudEnabled,
  } = useFlags();

  const dayHeaderItems: DayHeaderItemType[] = useMemo(() => {
    const weekStart = getUSWeekStartMonday(currentDate);
    return WEEKDAYS_ALTERNATIVE.map((day: number) => ({
      date: weekStart.plus({ days: day - 1 }),
      weekday: weekStart.plus({ days: day - 1 }).weekdayShort,
    }));
  }, [currentDate]);

  useEffect(() => {
    scrollToCurrentTime(scheduledItems);
  }, []);

  return (
    <ChakraFlex
      direction="column"
      height={height}
      id={TOP_LEVEL_ID}
      overflow="auto"
    >
      <ChakraFlex
        background="neutral.background.secondary"
        border={`1px solid ${neutral.line}`}
        borderRadius="16px 16px 0px 0px"
        height="36px"
        id={`${TOP_LEVEL_ID}__days`}
      >
        <ChakraBox width="5%" />
        {dayHeaderItems.map((item: DayHeaderItemType) => (
          <ScheduleWeekHeaderItem key={item.weekday} {...item} />
        ))}
      </ChakraFlex>
      <ChakraFlex id={timesContainerId} overflow="auto">
        <ChakraFlex
          flexDirection="column"
          id={`${TOP_LEVEL_ID}__times`}
          width="5%"
        >
          {WEEK_VIEW_TIMES.map((time: string) => (
            <ScheduleWeekTimeItem key={time} time={time} />
          ))}
        </ChakraFlex>
        {dayHeaderItems.map((item: DayHeaderItemType) => {
          const scheduledItemsByDay = scheduledItems.filter(
            (scheduledItem: ScheduleItemBase) =>
              DateTime.fromISO(scheduledItem.startDateTime).toLocaleString(
                DateTime.DATE_FULL,
              ) === item.date.toLocaleString(DateTime.DATE_FULL),
          );

          // Positioned once per day so a card spanning several slots is laid out
          // against every card it overlaps, not just its own slot's cards.
          const layoutUnits = getWeekViewDayLayout(
            scheduledItemsByDay,
            isViewCancelledAppointmentsInMaudEnabled,
          );

          return (
            <ChakraFlex flexDirection="column" key={item.weekday} width="15%">
              {WEEK_VIEW_TIMES.map((time: string, idx: number) => {
                const layoutUnitsByTime = layoutUnits.filter(
                  (unit: WeekViewLayoutUnit) =>
                    DateTime.fromISO(unit.startDateTime).hour === idx,
                );
                return (
                  <ScheduleWeekBodyItem
                    dateTime={`${item.weekday}-${idx}`}
                    isProvider={isProvider}
                    key={time}
                    layoutUnits={layoutUnitsByTime}
                    onCancelledGroupClicked={onCancelledGroupClicked}
                    onScheduleItemClicked={onScheduleItemClicked}
                  />
                );
              })}
            </ChakraFlex>
          );
        })}
      </ChakraFlex>
    </ChakraFlex>
  );
};

export default ScheduleWeekView;
